import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { ActionCreator } from '@ngrx/store';
import { catchError, of, map } from 'rxjs';

import { AuthenticatedUserResponse } from '@ninety/platform/models/auth/authenticated-user-response';
import { StorageService } from '@ninety/platform/services/services/storage.service';
import { TokenService } from '@ninety/platform/services/services/token.service';

import { BaseEffects } from '../../../base.effects';
import { SpinnerActions } from '../../spinner/spinner-state.actions';
import { getMicrosoftFederationUrl, MicrosoftAction } from '../services/microsoft';

import { LoginActions } from './login.actions';

const apiV4 = 'api/v4';

@Injectable()
export class LoginMicrosoftEffects extends BaseEffects {
  private readonly tokenService = inject(TokenService);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageService);

  microsoftRedirect$ = this.tapEffect({
    action: LoginActions.logInWithMicrosoft,
    tapFn: () => {
      const statePayload = encodeURIComponent(
        JSON.stringify({
          action: MicrosoftAction.LOGIN,
        })
      );
      const queryParams = new URLSearchParams(window.location.search);
      const redirectUrl = queryParams.get('redirectUrl');
      if (redirectUrl) this.storage.set('redirectUrl', redirectUrl);

      window.location.href = getMicrosoftFederationUrl(statePayload);
    },
  });

  returnFromMicrosoftSso$ = this.mapEffect({
    action: ROUTER_NAVIGATION as unknown as ActionCreator,
    filterFn: (action: RouterNavigationAction) => {
      const isLoginRoute = action.payload.routerState.url.toLowerCase().startsWith('/login');
      if (!isLoginRoute) return false;

      const code = new URL(window.location.href).searchParams.get('code');
      const state = new URL(window.location.href).searchParams.get('state');

      if (state) {
        const stateObj = JSON.parse(decodeURIComponent(state));
        if (stateObj.action === MicrosoftAction.ACCEPT_INVITE) {
          return false;
        }
      }
      if (code && state) return true;

      return false;
    },
    mapFn: () => {
      const state = new URL(window.location.href).searchParams.get('state');
      const parsedState = JSON.parse(state); // '{"action":"signUpWithMicrosoft"}'
      const code = new URL(window.location.href).searchParams.get('code');
      // Clear the OAuth code from the URL immediately so re-navigation can't re-dispatch it
      window.history.replaceState(window.history.state, '', window.location.pathname);
      return parsedState.action === MicrosoftAction.SIGN_UP
        ? LoginActions.signUpWithMicrosoftSsoResponse({ code })
        : LoginActions.logInWithMicrosoftSSOResponse({ code });
    },
  });

  loginWithMicrosoftSsoResponse$ = this.mapEffect({
    action: LoginActions.logInWithMicrosoftSSOResponse,
    mapFn: ({ code }) => {
      return !!code
        ? LoginActions.logInWithMicrosoftSuccess({ code })
        : LoginActions.logInWithMicrosoftFailed({
            error: { error: 'No code found', errorDescription: 'No code found in SSO response' },
          });
    },
  });

  logInWithMicrosoftSuccess$ = this.switchMapEffect({
    action: LoginActions.logInWithMicrosoftSuccess,
    switchMapFn: ({ code }) => {
      const payload = {
        idToken: null,
        microsoftCode: code,
        loginWithMicrosoft: true,
        signupWithMicrosoft: false,
      };

      return this.http.post<AuthenticatedUserResponse>(`${apiV4}/Login`, payload, { withCredentials: true }).pipe(
        map(response => {
          if (!response.tokens?.access) {
            return LoginActions.noMicrosoftToken();
          }
          this.tokenService.saveTokens(response.tokens);
          return LoginActions.logInSuccess({ response, isMicrosoft: true });
        }),
        catchError((error: unknown) => {
          return of(LoginActions.logInFailed({ error, idToken: null }));
        })
      );
    },
  });

  logInWithMicrosoftIdpTokenSuccess$ = this.switchMapEffect({
    action: LoginActions.logInWithMicrosoftIdpTokenSuccess,
    switchMapFn: ({ idToken }) => {
      const payload = {
        idToken: idToken,
        loginWithMicrosoft: true,
        signupWithMicrosoft: false,
      };

      return this.http.post<AuthenticatedUserResponse>(`${apiV4}/Login`, payload, { withCredentials: true }).pipe(
        map(response => {
          if (!response.tokens?.access) {
            return LoginActions.noMicrosoftToken();
          }
          this.tokenService.saveTokens(response.tokens);
          return LoginActions.logInSuccess({ response, isMicrosoft: true });
        }),
        catchError((error: unknown) => {
          return of(LoginActions.logInFailed({ error, idToken }));
        })
      );
    },
  });

  signUpWithMicrosoftSsoResponse$ = this.switchMapEffect({
    action: LoginActions.signUpWithMicrosoftSsoResponse,
    switchMapFn: ({ code }) => {
      const payload = {
        microsoftCode: code,
        loginWithMicrosoft: true,
        signupWithMicrosoft: true,
      };

      return this.http.post<AuthenticatedUserResponse>(`${apiV4}/Login`, payload, { withCredentials: true }).pipe(
        map(response => {
          this.router.navigate(
            ['/sign-up'],
            response.microsoftEmail
              ? { queryParams: { email: response.microsoftEmail, signUpWithMicrosoft: true } }
              : undefined
          );
          return LoginActions.signUpWithMicrosoftSuccess();
        }),
        catchError((error: unknown) => {
          return of(LoginActions.logInFailed({ error, idToken: null }));
        })
      );
    },
  });

  startSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.logInWithMicrosoftSSOResponse, LoginActions.signUpWithMicrosoftSsoResponse),
      map(({ type }) => SpinnerActions.startPrimary({ source: type }))
    )
  );

  stopSpinner = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.signUpWithMicrosoftSuccess),
      map(({ type }) => SpinnerActions.startPrimary({ source: type }))
    )
  );
}
